/**
 * Stored rule templates ↔ the library's UI shape. A template stores the rule config
 * in the **stored** shape, so creating a rule from one is a copy rather than a
 * translation, and reading it back reuses the rule read path.
 */
import {
	RuleDTO,
	RuleStatus,
	RuleTemplateDTO,
	RuleTemplateVisibility
} from 'api-models'
import { mapFetchedRules } from 'pages/workspace/asset/protection/components/rules-panel/components/rules-list/utils'
import { ToCustomRuleContext, toCustomRule } from './rule-adapters'
import { RuleEntities } from './rule-entities'
import { RuleDraft, RuleTemplate } from './types'

/** The config slice of a rule, taken straight off the stored rule exactly as
 *  Duplicate does, which keeps both flows clear of the write mapping (plan §2.2). */
export const ruleToTemplateConfig = (rule: RuleDTO) => ({
	name: rule.name,
	description: rule.description,
	resolve_status: rule.resolve_status,
	ownership: rule.ownership,
	focus: rule.focus,
	scope: rule.scope,
	criteria: rule.criteria,
	actions: rule.actions,
	type: rule.type
})

/** The stored config read back as a UI draft. `mapFetchedRules` wants a whole
 *  `RuleDTO`, so it is padded with fields a blueprint does not carry; they are inert. */
export const templateConfigToDraft = (
	config: RuleTemplateDTO['rule_data'],
	context: ToCustomRuleContext
): RuleDraft => {
	const asRule = {
		...config,
		id: '',
		asset_id: '',
		ad_account_id: null,
		campaign_id: null,
		status: RuleStatus.ACTIVE,
		criteria: config.criteria,
		actions: config.actions
	} as unknown as RuleDTO

	const [mapped] = mapFetchedRules([asRule])
	const rule = toCustomRule(mapped, context)

	return {
		name: rule.name,
		description: rule.description,
		scope: rule.scope,
		trigger: rule.trigger,
		criteria: rule.criteria,
		threshold: rule.threshold,
		actions: rule.actions
	}
}

/** One stored template as the library lists it. The tier is its **visibility**, not
 *  its authorship — your own shared template belongs in the Shared tab. Authorship
 *  decides only whether the row gets a three-dot menu. */
/**
 * One stored template → the library's shape, or `null` if its config cannot be read.
 *
 * The mapping dereferences `scope.type` and `criteria.modifiers` without guards
 * (`rules-list/utils.ts:48,60`) and runs for EVERY template inside one page-level memo,
 * so a single unreadable row threw during render and blanked the whole Custom Rules
 * page — for the entire workspace, once the row is shared. The API now refuses those
 * shapes; this is for the ones already stored, and for anything else that writes them.
 */
export const toRuleTemplate = (
	dto: RuleTemplateDTO,
	currentUserId: string | undefined,
	context: ToCustomRuleContext
): RuleTemplate | null => {
	try {
		return {
			id: dto.id,
			name: dto.name,
			description: dto.description,
			scope:
				dto.visibility === RuleTemplateVisibility.SHARED ? 'shared' : 'mine',
			author: dto.created_by === currentUserId ? 'You' : dto.created_by_name,
			assetId: dto.asset_id,
			draft: templateConfigToDraft(dto.rule_data, context)
		}
	} catch (error) {
		// Dropped from the library rather than rendered broken: the row is unusable
		// either way, and the page has no error boundary to fall back to.
		console.error(`Rule template ${dto.id} could not be read`, error)
		return null
	}
}

/** A template's entity ids belong to whichever website they were picked on, so they are
 *  RESOLVED against the current website's entities rather than trusted: what still
 *  exists is kept, the rest drops. The scope TYPE always survives — silently widening
 *  the rule to the whole site would be a different rule (§5).
 *
 *  Deliberately not a comparison of `asset_id`s. Editing a template from another website
 *  writes that site's ids under the original site's `asset_id`, and the pair then reads
 *  as "same site" on the wrong one (keeping ids that can never match a click) and
 *  "different site" on the right one (clearing a selection that was valid). Resolving
 *  the ids answers the only question that matters, and heals a template already stored
 *  that way without a migration. */
export const applyTemplateScope = (
	draft: RuleDraft,
	entities: RuleEntities
): RuleDraft => {
	if (draft.scope.mode === 'website') return draft
	const available = new Set(
		draft.scope.mode === 'campaigns'
			? entities.campaigns.map((campaign) => campaign.id)
			: entities.adAccounts.map((account) => account.id)
	)
	return {
		...draft,
		scope: {
			...draft.scope,
			values: draft.scope.values.filter((id) => available.has(id))
		}
	}
}
